import moment from 'moment'

const idexxInstrumentCodes = ['003', '014', '015', '017']

// 単位と基準値を持たない検査項目コードのリスト
// BAND L000105
// nRBC L000170
const itemCodesWithoutUnitAndRange = ['L000105', 'L000170']

export const hasUnitAndRange = masterLaboratoryItemCode => {
  return !itemCodesWithoutUnitAndRange.includes(masterLaboratoryItemCode)
}

// 定量限界値であることを示す表記が含まれているかを判定する関数
// loq (limit of quantitation)
export const isLoqValue = value => {
  const regex = /^([<>])\s*\d+(\.\d+)?$/
  return regex.test(value.trim())
}

// 無希釈測定の SAA では注釈を追記（下記 URL 参照）
// https://trello.com/c/FaW4r9Di
export const isInvalidDilution = measurementResultItem => {
  const { masterLaboratoryItemCode, dilutionRatio } = measurementResultItem
  return masterLaboratoryItemCode === 'L002225' && dilutionRatio === '01'
}

export const isNxMeasurementResult = measurementResult => {
  const nxInstrumentCode = '031'
  if (!measurementResult) {
    return false
  }
  return measurementResult.masterLaboratoryInstrumentCode === nxInstrumentCode
}

export const isOverMonthAgeRange = (
  laboratoryReferenceRange,
  patientMonthAge
) => {
  return (
    laboratoryReferenceRange.maxMonthAge !== -1 &&
    laboratoryReferenceRange.maxMonthAge < patientMonthAge
  )
}

// 検査結果の値が基準値超過であるかを判定する関数
export const isOverReferenceRange = (
  numberValue, // Number で数値化済み または NaN
  numberHigh, // Number で数値化済み または NaN
  loqQualifier // '', '<', '>' のみ
) => {
  if (isNaN(numberValue) || isNaN(numberHigh)) {
    return false
  } else {
    if (loqQualifier === '<') {
      return false
    }
    return numberHigh < numberValue
  }
}

export const isUnderMonthAgeRange = (
  laboratoryReferenceRange,
  patientMonthAge
) => {
  return (
    laboratoryReferenceRange.minMonthAge !== -1 &&
    patientMonthAge < laboratoryReferenceRange.minMonthAge
  )
}

// 検査結果の値が基準値未満であるかを判定する関数
export const isUnderReferenceRange = (
  numberValue, // Number で数値化済み または NaN
  numberLow, // Number で数値化済み または NaN
  loqQualifier // '', '<', '>' のみ
) => {
  if (isNaN(numberValue) || isNaN(numberLow)) {
    return false
  } else {
    if (loqQualifier === '>') {
      return false
    }
    return numberValue < numberLow
  }
}

// 検査日時点での患者月齢が laboratoryReferenceRange の月齢期間内に位置するか
export const isWithinMonthAgeRange = (
  laboratoryReferenceRange,
  patientBirthday,
  runDate
) => {
  const runDateMoment = moment(runDate, 'YYYYMMDD')
  if (patientBirthday.length === 8) {
    const birthdayMoment = moment(patientBirthday, 'YYYYMMDD')
    const monthAge = runDateMoment.diff(birthdayMoment, 'months')
    return (
      !isUnderMonthAgeRange(laboratoryReferenceRange, monthAge) &&
      !isOverMonthAgeRange(laboratoryReferenceRange, monthAge)
    )
  } else if (patientBirthday.length === 6) {
    const minBirthdayMoment = moment(patientBirthday, 'YYYYMM').startOf('month')
    const maxBirthdayMoment = moment(patientBirthday, 'YYYYMM')
      .endOf('month')
      .startOf('day')
    const minMonthAge = runDateMoment.diff(maxBirthdayMoment, 'months')
    const maxMonthAge = runDateMoment.diff(minBirthdayMoment, 'months')
    return (
      !isUnderMonthAgeRange(laboratoryReferenceRange, minMonthAge) &&
      !isOverMonthAgeRange(laboratoryReferenceRange, maxMonthAge)
    )
  } else if (patientBirthday.length === 4) {
    const minBirthdayMoment = moment(patientBirthday, 'YYYY').startOf('year')
    const maxBirthdayMoment = moment(patientBirthday, 'YYYY')
      .endOf('year')
      .startOf('day')
    const minMonthAge = runDateMoment.diff(maxBirthdayMoment, 'months')
    const maxMonthAge = runDateMoment.diff(minBirthdayMoment, 'months')
    return (
      !isUnderMonthAgeRange(laboratoryReferenceRange, minMonthAge) &&
      !isOverMonthAgeRange(laboratoryReferenceRange, maxMonthAge)
    )
  } else {
    if (
      laboratoryReferenceRange.minMonthAge === -1 &&
      laboratoryReferenceRange.maxMonthAge === -1
    ) {
      return true
    } else {
      return false
    }
  }
}

export const shouldDisplayOnlyValue = masterLaboratoryInstrumentCode => {
  return idexxInstrumentCodes.includes(masterLaboratoryInstrumentCode)
}

// 機器コードが 036 の場合 030 に変換する
// それ以外は機器コードをそのまま返す
export const toCommonInstrumentCode = masterLaboratoryInstrumentCode => {
  if (masterLaboratoryInstrumentCode === '036') {
    return '030'
  }
  return masterLaboratoryInstrumentCode
}

// 基準範囲比較用の数値型の値に変換
export const toComparisonNumber = string => {
  if (string.trim() === '') return NaN
  if (isLoqValue(string)) {
    const numberPart = string.trim().replace(/[<>]\s*/, '')
    return Number(numberPart)
  }
  if (isNaN(string)) return NaN
  return Number(string)
}

export const toDisplayItemName = (itemName, measurementResultItem) => {
  if (!itemName) {
    return ''
  }
  return isInvalidDilution(measurementResultItem)
    ? `${itemName} ※無希釈測定のため`
    : itemName
}

// 現状表記する記号は「<」と「>」のみのため、それ以外は空文字を返す
export const toDisplayQualifier = qualifier => {
  if (qualifier === '<' || qualifier === '>') {
    return qualifier
  } else {
    return ''
  }
}

export const toDisplayRange = (low, high) => {
  if (!low && !high) {
    return ''
  } else {
    return `${low} – ${high}`
  }
}

export const toDisplayValue = (
  masterLaboratoryInstrumentCode,
  measurementResultItem
) => {
  if (isInvalidDilution(measurementResultItem)) {
    return '※'
  }
  const { value, qualifier } = measurementResultItem
  if (shouldDisplayOnlyValue(masterLaboratoryInstrumentCode)) {
    return value
  }
  const displayQualifier = toDisplayQualifier(qualifier)
  const displayValue = [displayQualifier, value].filter(Boolean).join(' ')
  return displayValue
}

export const toDisplayValueInPrint = (
  masterLaboratoryInstrumentCode,
  measurementResultItem,
  referenceRangeLow,
  referenceRangeHigh
) => {
  const displayValue = toDisplayValue(
    masterLaboratoryInstrumentCode,
    measurementResultItem
  )
  if (displayValue === '※') {
    return displayValue
  }
  const numberValue = toComparisonNumber(measurementResultItem.value)
  const numberLow = toComparisonNumber(referenceRangeLow)
  const numberHigh = toComparisonNumber(referenceRangeHigh)
  const loqQualifier = toLoqQualifier(displayValue)
  if (isUnderReferenceRange(numberValue, numberLow, loqQualifier)) {
    return displayValue + ' ↓'
  } else if (isOverReferenceRange(numberValue, numberHigh, loqQualifier)) {
    return displayValue + ' ↑'
  } else {
    return displayValue
  }
}

export const toItemDefaultValues = (laboratoryItem, laboratoryItemRange) => {
  const unit = laboratoryItem?.unit ?? ''
  const low = laboratoryItemRange?.low ?? ''
  const high = laboratoryItemRange?.high ?? ''
  return { unit, low, high }
}

export const toLoqQualifier = displayValue => {
  if (isLoqValue(displayValue)) {
    return displayValue.trim().charAt(0)
  } else {
    return ''
  }
}

export const useSettingDefaultValues = (
  laboratoryDefaultValueSetting,
  measurementResult
) => {
  const useInstrumentUnitReference =
    laboratoryDefaultValueSetting.useInstrumentUnitReferenceFlg
  const hasAssigned = measurementResult.hasAssignedFlg
  const speciesCode = measurementResult.masterLaboratorySpeciesCode
  return !useInstrumentUnitReference && !hasAssigned && !!speciesCode
}
